import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Grid,
  Box,
  SelectChangeEvent
} from '@mui/material';
import { ScheduleSlot, ScheduleSlotType, CreateScheduleSlotDto, UpdateScheduleSlotDto } from '../../types/calendar';
import { ModalIndices } from '../../common/i-doc/i-doc.registry.ts';

interface Props {
  open: boolean;
  onClose: () => void;
  onSave: (data: CreateScheduleSlotDto | (UpdateScheduleSlotDto & { id: number })) => void;
  onDelete?: (id: number) => void; // Optional: for deleting
  slotData: Partial<ScheduleSlot> | Partial<CreateScheduleSlotDto> | null;
  slotTypes: ScheduleSlotType[];
}

const emptySlot: Partial<CreateScheduleSlotDto> = {
    timeStart: '08:00',
    durationMinutes: 30,
    notificationEnabled: false,
    notificationMinutesBefore: 15,
    slotNameI18n: '',
};

export const ScheduleSlotEditModal: React.FC<Props> = ({ open, onClose, onSave, onDelete, slotData, slotTypes }) => {
  const [formData, setFormData] = useState<Partial<ScheduleSlot> | Partial<CreateScheduleSlotDto>>(emptySlot);

  useEffect(() => {
    if (slotData) {
        const initialData = { ...emptySlot, ...slotData };
        if (initialData.timeStart && initialData.timeStart.length > 5) {
            initialData.timeStart = initialData.timeStart.substring(0, 5);
        }
        setFormData(initialData);
    } else {
        setFormData(emptySlot);
    }
  }, [slotData, open]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;

    setFormData(prev => ({
      ...prev,
      [name as string]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSelectChange = (e: SelectChangeEvent<number | ''>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSave = () => {
    // @ts-ignore
    onSave(formData);
  };

  const handleDelete = () => {
    if (onDelete && 'id' in formData && formData.id) {
        onDelete(formData.id);
    }
  };

  const isNew = !('id' in (slotData || {}));

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth data-i-doc={ModalIndices.SCHEDULE_SLOT_EDIT}>
      <DialogTitle>{isNew ? 'Створити новий слот' : 'Редагувати слот'}</DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2} sx={{ pt: 1 }}>
            <Grid item xs={12}>
                <FormControl fullWidth required>
                    <InputLabel id="slot-type-label">Тип слоту</InputLabel>
                    <Select
                        labelId="slot-type-label"
                        name="slotTypeId"
                        value={('slotTypeId' in formData ? formData.slotTypeId : (formData as ScheduleSlot).slotType?.id) || ''}
                        label="Тип слоту"
                        onChange={handleSelectChange}
                        disabled={!isNew}
                    >
                        {slotTypes.map(type => (
                            <MenuItem key={type.id} value={type.id}>
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <Box component="span" sx={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: type.color, mr: 1.5 }} />
                                    {type.name}
                                </Box>
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
                <TextField
                    name="timeStart"
                    label="Час початку"
                    type="time"
                    value={formData.timeStart || ''}
                    onChange={handleChange}
                    fullWidth
                    required
                    InputLabelProps={{ shrink: true }}
                />
            </Grid>
            <Grid item xs={12} sm={6}>
                <TextField
                    name="durationMinutes"
                    label="Тривалість (хв)"
                    type="number"
                    value={formData.durationMinutes || ''}
                    onChange={handleChange}
                    fullWidth
                />
            </Grid>
            <Grid item xs={12}>
                 <TextField
                    name="slotNameI18n"
                    label="Кастомна назва (опціонально)"
                    value={(formData as any).slotNameI18n || (formData as ScheduleSlot).slotName || ''}
                    onChange={handleChange}
                    fullWidth
                    placeholder={slotTypes.find(st => st.id === (isNew ? (formData as CreateScheduleSlotDto).slotTypeId : (formData as ScheduleSlot).slotType?.id))?.name}
                />
            </Grid>
            <Grid item xs={12}>
                <FormControlLabel
                    control={<Switch checked={!!formData.notificationEnabled} onChange={handleChange} name="notificationEnabled" />}
                    label="Увімкнути нагадування"
                />
            </Grid>
            {formData.notificationEnabled && (
                <Grid item xs={12}>
                    <TextField
                        name="notificationMinutesBefore"
                        label="Нагадати за (хв)"
                        type="number"
                        value={formData.notificationMinutesBefore || ''}
                        onChange={handleChange}
                        fullWidth
                    />
                </Grid>
            )}
        </Grid>
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'space-between' }}>
        <Box>
            {!isNew && onDelete && (
                <Button onClick={handleDelete} color="error">
                    Видалити
                </Button>
            )}
        </Box>
        <Box>
            <Button onClick={onClose}>Скасувати</Button>
            <Button onClick={handleSave} variant="contained">Зберегти</Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};
